import { useEffect, useState, useCallback } from 'react';
import {
  SimpleGrid,
  Card,
  Group,
  Text,
  Badge,
  Table,
  ThemeIcon,
  Stack,
  RingProgress,
  Skeleton,
  Box,
  ActionIcon,
  Tooltip,
  Transition,
} from '@mantine/core';
import {
  IconUsers,
  IconRoute,
  IconClockHour4,
  IconCheck,
  IconX,
  IconLoader2,
  IconAlertTriangle,
  IconRefresh,
  IconTrendingUp,
  IconBolt,
} from '@tabler/icons-react';
import { useArchbaseRemoteServiceApi } from '@archbase/data';
import { RouteXDashboardService, type SystemStatus } from '@services/DashboardService';
import { RouteXRouteJobService } from '@services/RouteJobService';
import { RouteXApiClientService } from '@services/ApiClientService';
import { API_TYPE } from '@ioc/IOCTypes';
import type { RouteJobDto } from '@domain/routeJob/RouteJobDto';

import classes from './DashboardView.module.css';

const STATUS_CONFIG = {
  UP: { color: 'teal', icon: IconCheck, label: 'Operacional' },
  DOWN: { color: 'red', icon: IconX, label: 'Indisponível' },
  DEGRADED: { color: 'yellow', icon: IconAlertTriangle, label: 'Degradado' },
} as const;

const JOB_STATUS_CONFIG: Record<string, { color: string; label: string }> = {
  SOLVED: { color: 'teal', label: 'Resolvido' },
  SOLVING: { color: 'blue', label: 'Processando' },
  PENDING: { color: 'yellow', label: 'Pendente' },
  FAILED: { color: 'red', label: 'Falhou' },
  CANCELLED: { color: 'gray', label: 'Cancelado' },
};

interface StatCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: React.ElementType;
  color: string;
  trend?: string;
}

function StatCard({ title, value, subtitle, icon: Icon, color, trend }: StatCardProps) {
  return (
    <Card className={classes.statCard} padding="lg" radius="sm" withBorder>
      <Group justify="space-between" align="flex-start">
        <Stack gap={4}>
          <Text className={classes.statLabel}>{title}</Text>
          <Text className={classes.statValue}>{value}</Text>
          {subtitle && (
            <Text size="xs" c="dimmed">
              {subtitle}
            </Text>
          )}
        </Stack>
        {/* A cor aqui e decoracao: o icone nao codifica dado nenhum, e a forma (pessoas,
            rota, raio, relogio) ja distingue os cartoes. Por isso todos usam a cor da marca.
            Antes eram blue/violet/teal/orange, o que colidia com o significado que essas
            mesmas cores tem no grafico de status ao lado — verde = resolvido, azul =
            processando, vermelho = falho. Cor decorativa competindo com cor semantica faz o
            leitor nao saber qual das duas quer dizer algo. */}
        <ThemeIcon size={44} radius="sm" variant="light" color={color}>
          <Icon size={24} stroke={1.5} />
        </ThemeIcon>
      </Group>
      {trend && (
        <Group gap={4} mt="sm">
          <IconTrendingUp size={14} color="var(--mantine-color-teal-6)" />
          <Text size="xs" c="teal">
            {trend}
          </Text>
        </Group>
      )}
    </Card>
  );
}

function formatAgo(iso?: string): string {
  if (!iso) return '-';
  const diff = Date.now() - new Date(iso).getTime();
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return `${sec}s`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} min`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h`;
  return `${Math.floor(hr / 24)}d`;
}

function formatTime(ms?: number): string {
  if (!ms) return '-';
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

export default function DashboardView() {
  const [status, setStatus] = useState<SystemStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [mounted, setMounted] = useState(false);
  const [jobs, setJobs] = useState<RouteJobDto[]>([]);
  const [clientCount, setClientCount] = useState<number | null>(null);

  const dashboardService = useArchbaseRemoteServiceApi<RouteXDashboardService>(API_TYPE.RouteXDashboard);
  const jobService = useArchbaseRemoteServiceApi<RouteXRouteJobService>(API_TYPE.RouteXRouteJob);
  const clientService = useArchbaseRemoteServiceApi<RouteXApiClientService>(API_TYPE.RouteXApiClient);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [statusResult, jobsResult, clientsResult] = await Promise.allSettled([
        dashboardService.getStatus(),
        jobService.findAll(0, 20),
        clientService.findAll(0, 1),
      ]);

      if (statusResult.status === 'fulfilled') {
        setStatus(statusResult.value);
      } else {
        setStatus({ service: 'routex-api', version: '-', status: 'DOWN', timestamp: new Date().toISOString() });
      }

      if (jobsResult.status === 'fulfilled') {
        setJobs(jobsResult.value?.content ?? []);
      }

      if (clientsResult.status === 'fulfilled') {
        setClientCount((clientsResult.value as any)?.totalElements ?? 0);
      }
    } finally {
      setLoading(false);
    }
  }, [dashboardService, jobService, clientService]);

  useEffect(() => {
    fetchAll();
    setMounted(true);
    const interval = setInterval(fetchAll, 30000);
    return () => clearInterval(interval);
  }, [fetchAll]);

  const systemUp = status?.status === 'UP';
  const statusCfg = STATUS_CONFIG[status?.status as keyof typeof STATUS_CONFIG] ?? STATUS_CONFIG.DOWN;

  const todayStart = new Date();
  todayStart.setHours(0, 0, 0, 0);
  const jobsToday = jobs.filter((j) => j.createdAt && new Date(j.createdAt) >= todayStart).length;
  const solved = jobs.filter((j) => j.status === 'SOLVED');
  const finished = jobs.filter((j) => j.status === 'SOLVED' || j.status === 'FAILED');
  const successRate = finished.length > 0 ? Math.round((solved.length / finished.length) * 1000) / 10 : null;
  const avgTime = solved.length > 0
    ? solved.reduce((acc, j) => acc + (j.solvingTimeMs ?? 0), 0) / solved.length
    : null;

  const recentJobs = jobs.slice(0, 6);

  return (
    <Transition mounted={mounted} transition="fade" duration={300}>
      {(transitionStyles) => (
        <Box p="xl" style={{ ...transitionStyles, height: '100%' }}>
          <Stack gap="xl">
            <Group justify="space-between">
              <Stack gap={2}>
                <Text className={classes.pageTitle}>Dashboard</Text>
                <Text size="sm" c="dimmed">
                  Visão geral do RouteX.dev
                </Text>
              </Stack>
              <Tooltip label="Atualizar">
                <ActionIcon variant="subtle" size="lg" onClick={fetchAll} loading={loading}>
                  <IconRefresh size={18} />
                </ActionIcon>
              </Tooltip>
            </Group>

            <Card className={`${classes.statusBanner} ${systemUp ? classes.statusUp : classes.statusDown}`} padding="md" radius="sm" withBorder>
              <Group justify="space-between">
                <Group gap="sm">
                  <Box className={`${classes.pulse} ${systemUp ? classes.pulseGreen : classes.pulseRed}`} />
                  <Stack gap={0}>
                    <Group gap="xs">
                      <Text fw={600} size="sm">
                        {status?.service ?? 'routex-api'}
                      </Text>
                      <Badge size="xs" variant="light" color={statusCfg.color}>
                        {statusCfg.label}
                      </Badge>
                    </Group>
                    <Text size="xs" c="dimmed" ff="monospace">
                      v{status?.version ?? '-'}
                    </Text>
                  </Stack>
                </Group>
                <Group gap="lg">
                  <Stack gap={0} align="center">
                    <Text size="xs" c="dimmed">Último check</Text>
                    <Text size="xs" ff="monospace">
                      {status?.timestamp ? new Date(status.timestamp).toLocaleTimeString('pt-BR') : '-'}
                    </Text>
                  </Stack>
                </Group>
              </Group>
            </Card>

            {loading ? (
              <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }}>
                {[...Array(4)].map((_, i) => (
                  <Skeleton key={i} height={120} radius="sm" />
                ))}
              </SimpleGrid>
            ) : (
              <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }}>
                <StatCard
                  title="API Clients"
                  value={clientCount ?? '—'}
                  subtitle="Cadastrados"
                  icon={IconUsers}
                  color="appPrimary"
                />
                <StatCard
                  title="Jobs Hoje"
                  value={jobsToday}
                  subtitle={`${jobs.length} nas últimas execuções`}
                  icon={IconRoute}
                  color="appPrimary"
                />
                <StatCard
                  title="Taxa de Sucesso"
                  value={successRate !== null ? `${successRate}%` : '—'}
                  subtitle={finished.length > 0 ? `${finished.length} finalizados` : 'Sem dados'}
                  icon={IconBolt}
                  color="appPrimary"
                />
                <StatCard
                  title="Tempo Médio"
                  value={avgTime !== null ? formatTime(avgTime) : '—'}
                  subtitle={solved.length > 0 ? `${solved.length} resolvidos` : 'Sem dados'}
                  icon={IconClockHour4}
                  color="appPrimary"
                />
              </SimpleGrid>
            )}

            <SimpleGrid cols={{ base: 1, lg: 3 }}>
              <Card padding="lg" radius="sm" withBorder>
                <Text fw={600} size="sm" mb="md">
                  Status dos Jobs
                </Text>
                <Group justify="center" mb="md">
                  <RingProgress
                    size={140}
                    thickness={12}
                    roundCaps
                    sections={(() => {
                      if (jobs.length === 0) return [{ value: 100, color: 'gray.3' }];
                      const pct = (n: number) => Math.round((n / jobs.length) * 100);
                      return [
                        { value: pct(solved.length), color: 'teal', tooltip: `Resolvidos — ${solved.length}` },
                        { value: pct(jobs.filter((j) => j.status === 'SOLVING').length), color: 'blue', tooltip: 'Processando' },
                        { value: pct(jobs.filter((j) => j.status === 'PENDING').length), color: 'yellow', tooltip: 'Pendentes' },
                        { value: pct(jobs.filter((j) => j.status === 'FAILED').length), color: 'red', tooltip: 'Falhos' },
                      ];
                    })()}
                    label={
                      <Stack align="center" gap={0}>
                        <Text size="xl" fw={700}>{jobs.length}</Text>
                        <Text size="xs" c="dimmed">jobs</Text>
                      </Stack>
                    }
                  />
                </Group>
                <Stack gap="xs">
                  {[
                    { key: 'SOLVED', label: 'Resolvidos', color: 'teal' },
                    { key: 'SOLVING', label: 'Processando', color: 'blue' },
                    { key: 'PENDING', label: 'Pendentes', color: 'yellow' },
                    { key: 'FAILED', label: 'Falhos', color: 'red' },
                  ].map((m) => {
                    const count = jobs.filter((j) => j.status === m.key).length;
                    return (
                      <Group key={m.key} justify="space-between">
                        <Group gap="xs">
                          <Box w={8} h={8} style={{ borderRadius: '50%', backgroundColor: `var(--mantine-color-${m.color}-6)` }} />
                          <Text size="xs">{m.label}</Text>
                        </Group>
                        <Text size="xs" ff="monospace" fw={500}>{count}</Text>
                      </Group>
                    );
                  })}
                </Stack>
              </Card>

              <Card padding="lg" radius="sm" withBorder style={{ gridColumn: 'span 2' }}>
                <Group justify="space-between" mb="md">
                  <Text fw={600} size="sm">Jobs Recentes</Text>
                  <Badge variant="light" size="sm">Tempo real</Badge>
                </Group>
                <Table.ScrollContainer minWidth={500}>
                  <Table verticalSpacing="xs" highlightOnHover>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>Job</Table.Th>
                        <Table.Th>Client</Table.Th>
                        <Table.Th ta="center">Veículos</Table.Th>
                        <Table.Th ta="center">Visitas</Table.Th>
                        <Table.Th>Status</Table.Th>
                        <Table.Th ta="right">Tempo</Table.Th>
                        <Table.Th ta="right">Há</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {recentJobs.length === 0 && !loading && (
                        <Table.Tr>
                          <Table.Td colSpan={7}>
                            <Text ta="center" c="dimmed" py="md" size="xs">Nenhum job executado</Text>
                          </Table.Td>
                        </Table.Tr>
                      )}
                      {recentJobs.map((job) => {
                        const jobCfg = JOB_STATUS_CONFIG[job.status] ?? { color: 'gray', label: job.status };
                        return (
                          <Table.Tr key={job.id} style={{ cursor: 'pointer' }}>
                            <Table.Td>
                              <Text size="xs" ff="monospace" fw={600}>{job.id.substring(0, 8)}</Text>
                            </Table.Td>
                            <Table.Td><Text size="xs">{job.clientId}</Text></Table.Td>
                            <Table.Td ta="center"><Text size="xs" ff="monospace">{job.vehiclesCount ?? '-'}</Text></Table.Td>
                            <Table.Td ta="center"><Text size="xs" ff="monospace">{job.visitsCount ?? '-'}</Text></Table.Td>
                            <Table.Td>
                              <Badge
                                size="xs"
                                variant="light"
                                color={jobCfg.color}
                                leftSection={job.status === 'SOLVING' ? <IconLoader2 size={10} className={classes.spin} /> : undefined}
                              >
                                {jobCfg.label}
                              </Badge>
                            </Table.Td>
                            <Table.Td ta="right"><Text size="xs" ff="monospace">{formatTime(job.solvingTimeMs)}</Text></Table.Td>
                            <Table.Td ta="right"><Text size="xs" c="dimmed">{formatAgo(job.createdAt)}</Text></Table.Td>
                          </Table.Tr>
                        );
                      })}
                    </Table.Tbody>
                  </Table>
                </Table.ScrollContainer>
              </Card>
            </SimpleGrid>
          </Stack>
        </Box>
      )}
    </Transition>
  );
}
